import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  FiFilter, FiX, FiShoppingCart, FiStar,
  FiPackage, FiGrid, FiList, FiTag, FiHeart,
  FiLogIn, FiUserPlus, FiArrowRight, FiAlertCircle,
  FiEye, FiEyeOff, FiChevronLeft,
} from "react-icons/fi";
import { useCart, useCustomer } from "../context/ShopContext";
import ShopTopBar from "../components/ShopTopBar";

/* ── PNG logos — used in sidebar brand-filter buttons ── */
import logoFord       from "../assets/uploads/cars/ford.png";
import logoVolkswagen from "../assets/uploads/cars/volkswagen.png";
import logoAudi       from "../assets/uploads/cars/audi.png";
import logoSuzuki     from "../assets/uploads/cars/suzuki.png";
import logoHyundai    from "../assets/uploads/cars/hyundai.png";
import logoHonda      from "../assets/uploads/cars/honda.png";
import logoBMW        from "../assets/uploads/cars/bmw.png";
import logoKia        from "../assets/uploads/cars/kia.png";
import logoMercedes   from "../assets/uploads/cars/mercedes1.png";
import logoToyota     from "../assets/uploads/cars/toyota1.png";
import logoNissan     from "../assets/uploads/cars/nissan.png";
import logoMazda      from "../assets/uploads/cars/mazda.png";
import logoChevrolet  from "../assets/uploads/cars/chevrolet.png";
import logoChery      from "../assets/uploads/cars/cherry.png";
import logoJeep       from "../assets/uploads/cars/jeep.png";
import logoGwm        from "../assets/uploads/cars/gwm.png";

/* ── SVG logos — used in the "Vehicles We Specialise On" marquee ── */
import svgAudi       from "../assets/uploads/cars/audi.svg";
import svgBmw        from "../assets/uploads/cars/bmw.svg";
import svgFord       from "../assets/uploads/cars/ford.svg";
import svgHonda      from "../assets/uploads/cars/honda.svg";
import svgHyundai    from "../assets/uploads/cars/hyundai.svg";
import svgKia        from "../assets/uploads/cars/kia.svg";
import svgMazda      from "../assets/uploads/cars/mazda.svg";
import svgMercedes   from "../assets/uploads/cars/mercedes.svg";
import svgNissan     from "../assets/uploads/cars/nissan.svg";
import svgSuzuki     from "../assets/uploads/cars/suzuki.svg";
import svgToyota     from "../assets/uploads/cars/toyota.svg";
import svgVolkswagen from "../assets/uploads/cars/volkswagen.svg";

// Canonical display name → matches car_make values in DB (Title Case)
// src = PNG used for sidebar filter buttons
const CAR_BRANDS = [
  { name: "Ford",       src: logoFord       },
  { name: "Volkswagen", src: logoVolkswagen },
  { name: "Audi",       src: logoAudi       },
  { name: "Suzuki",     src: logoSuzuki     },
  { name: "Hyundai",    src: logoHyundai    },
  { name: "Honda",      src: logoHonda      },
  { name: "BMW",        src: logoBMW        },
  { name: "Kia",        src: logoKia        },
  { name: "Mercedes",   src: logoMercedes   },
  { name: "Toyota",     src: logoToyota     },
  { name: "Nissan",     src: logoNissan     },
  { name: "Mazda",      src: logoMazda      },
  { name: "Chevrolet",  src: logoChevrolet  },
  { name: "Chery",      src: logoChery      },
  { name: "Jeep",       src: logoJeep       },
  { name: "GWM",        src: logoGwm        },
];

// Brands that have an SVG — used only in the marquee
const MARQUEE_BRANDS = [
  { name: "Audi",       svg: svgAudi       },
  { name: "BMW",        svg: svgBmw        },
  { name: "Ford",       svg: svgFord       },
  { name: "Honda",      svg: svgHonda      },
  { name: "Hyundai",    svg: svgHyundai    },
  { name: "Kia",        svg: svgKia        },
  { name: "Mazda",      svg: svgMazda      },
  { name: "Mercedes",   svg: svgMercedes   },
  { name: "Nissan",     svg: svgNissan     },
  { name: "Suzuki",     svg: svgSuzuki     },
  { name: "Toyota",     svg: svgToyota     },
  { name: "Volkswagen", svg: svgVolkswagen },
];

import "./Shop.css";

function shopImg(filename) {
  if (!filename) return null;
  return `${process.env.REACT_APP_API_URL}/uploads/shop/${filename}`;
}

const fmt = (n) =>
  "R " + Number(n).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const stockLabel = (qty, low) => {
  if (qty === 0)  return { label: "Out of stock", cls: "tag--out" };
  if (qty <= low) return { label: "Low stock",    cls: "tag--low" };
  return                  { label: "Available",   cls: "tag--in"  };
};

/* ══════════════════════════════════════
   WISHLIST AUTH MODAL
   Shown when a guest user tries to add
   to wishlist — no Guest option.
   ══════════════════════════════════════ */
function WishlistAuthModal({ onClose, onSuccess }) {
  const { login } = useCustomer();
  const [mode,    setMode]    = useState("choose"); // "choose" | "login" | "register"
  const [showPw,  setShowPw]  = useState(false);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm,   setRegForm]   = useState({
    first_name: "", last_name: "", email: "", phone: "",
    password: "", password2: "",
  });

  // Close on backdrop click
  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const r    = await fetch(`${process.env.REACT_APP_API_URL}/api/customers/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(loginForm),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || "Login failed.");
      login(data.customer, data.token);
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (regForm.password !== regForm.password2) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const r    = await fetch(`${process.env.REACT_APP_API_URL}/api/customers/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(regForm),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || "Registration failed.");
      login(data.customer, data.token);
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="wishlist-modal__backdrop" onClick={handleBackdrop}>
      <div className="wishlist-modal">
        {/* Header */}
        <div className="wishlist-modal__header">
          <div className="wishlist-modal__header-left">
            <FiHeart size={18} className="wishlist-modal__heart" />
            <span>Save to Wishlist</span>
          </div>
          <button className="wishlist-modal__close" onClick={onClose} aria-label="Close">
            <FiX size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="wishlist-modal__body">

          {mode === "choose" && (
            <>
              <p className="wishlist-modal__intro">
                Sign in or create a free account to save items to your wishlist.
              </p>
              <div className="wishlist-modal__options">
                <button className="wishlist-modal__opt" onClick={() => setMode("login")}>
                  <div className="wishlist-modal__opt-icon"><FiLogIn size={22} /></div>
                  <div className="wishlist-modal__opt-text">
                    <strong>Sign In</strong>
                    <span>Use your existing account</span>
                  </div>
                  <FiArrowRight size={15} className="wishlist-modal__opt-arrow" />
                </button>
                <button className="wishlist-modal__opt" onClick={() => setMode("register")}>
                  <div className="wishlist-modal__opt-icon"><FiUserPlus size={22} /></div>
                  <div className="wishlist-modal__opt-text">
                    <strong>Create Account</strong>
                    <span>It's free and only takes a minute</span>
                  </div>
                  <FiArrowRight size={15} className="wishlist-modal__opt-arrow" />
                </button>
              </div>
            </>
          )}

          {mode === "login" && (
            <>
              <button className="wishlist-modal__back" onClick={() => { setMode("choose"); setError(""); }}>
                <FiChevronLeft size={14} /> Back
              </button>
              <h3 className="wishlist-modal__form-title">Sign In</h3>
              <form className="wishlist-modal__form" onSubmit={handleLogin}>
                {error && (
                  <p className="wishlist-modal__error">
                    <FiAlertCircle size={13} /> {error}
                  </p>
                )}
                <div className="wishlist-modal__field">
                  <label>Email address</label>
                  <input
                    type="email"
                    required
                    value={loginForm.email}
                    onChange={e => setLoginForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="you@example.com"
                  />
                </div>
                <div className="wishlist-modal__field">
                  <label>Password</label>
                  <div className="wishlist-modal__pw-wrap">
                    <input
                      type={showPw ? "text" : "password"}
                      required
                      value={loginForm.password}
                      onChange={e => setLoginForm(f => ({ ...f, password: e.target.value }))}
                      placeholder="Your password"
                    />
                    <button type="button" className="wishlist-modal__pw-toggle" onClick={() => setShowPw(v => !v)}>
                      {showPw ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                    </button>
                  </div>
                </div>
                <button className="wishlist-modal__submit" disabled={loading}>
                  {loading ? "Signing in…" : "Sign In"}
                </button>
                <p className="wishlist-modal__switch">
                  Don't have an account?{" "}
                  <button type="button" onClick={() => { setMode("register"); setError(""); }}>
                    Create one
                  </button>
                </p>
              </form>
            </>
          )}

          {mode === "register" && (
            <>
              <button className="wishlist-modal__back" onClick={() => { setMode("choose"); setError(""); }}>
                <FiChevronLeft size={14} /> Back
              </button>
              <h3 className="wishlist-modal__form-title">Create Account</h3>
              <form className="wishlist-modal__form wishlist-modal__form--grid" onSubmit={handleRegister}>
                {error && (
                  <p className="wishlist-modal__error wishlist-modal__error--full">
                    <FiAlertCircle size={13} /> {error}
                  </p>
                )}
                <div className="wishlist-modal__field">
                  <label>First name *</label>
                  <input
                    required
                    value={regForm.first_name}
                    onChange={e => setRegForm(f => ({ ...f, first_name: e.target.value }))}
                  />
                </div>
                <div className="wishlist-modal__field">
                  <label>Last name *</label>
                  <input
                    required
                    value={regForm.last_name}
                    onChange={e => setRegForm(f => ({ ...f, last_name: e.target.value }))}
                  />
                </div>
                <div className="wishlist-modal__field wishlist-modal__field--full">
                  <label>Email address *</label>
                  <input
                    type="email"
                    required
                    value={regForm.email}
                    onChange={e => setRegForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="you@example.com"
                  />
                </div>
                <div className="wishlist-modal__field wishlist-modal__field--full">
                  <label>Phone number</label>
                  <input
                    value={regForm.phone}
                    onChange={e => setRegForm(f => ({ ...f, phone: e.target.value }))}
                  />
                </div>
                <div className="wishlist-modal__field">
                  <label>Password *</label>
                  <div className="wishlist-modal__pw-wrap">
                    <input
                      type={showPw ? "text" : "password"}
                      required
                      value={regForm.password}
                      onChange={e => setRegForm(f => ({ ...f, password: e.target.value }))}
                      placeholder="Min. 8 characters"
                    />
                    <button type="button" className="wishlist-modal__pw-toggle" onClick={() => setShowPw(v => !v)}>
                      {showPw ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                    </button>
                  </div>
                </div>
                <div className="wishlist-modal__field">
                  <label>Confirm password *</label>
                  <input
                    type={showPw ? "text" : "password"}
                    required
                    value={regForm.password2}
                    onChange={e => setRegForm(f => ({ ...f, password2: e.target.value }))}
                    placeholder="Re-enter password"
                  />
                </div>
                <button className="wishlist-modal__submit wishlist-modal__field--full" disabled={loading}>
                  {loading ? "Creating account…" : "Create Account"}
                </button>
                <p className="wishlist-modal__switch wishlist-modal__field--full">
                  Already have an account?{" "}
                  <button type="button" onClick={() => { setMode("login"); setError(""); }}>
                    Sign in
                  </button>
                </p>
              </form>
            </>
          )}

        </div>
      </div>
    </div>
  );
}

/* ── Wishlist hook ── */
function useWishlist() {
  const { customer, token } = useCustomer();
  const [wishlistIds, setWishlistIds] = useState(new Set());
  const [pending,     setPending]     = useState(new Set());

  useEffect(() => {
    if (!customer || !token) { setWishlistIds(new Set()); return; }
    fetch(`${process.env.REACT_APP_API_URL}/api/customers/me/wishlist`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) setWishlistIds(new Set(data.map(p => p.product_id)));
      })
      .catch(() => {});
  }, [customer, token]);

  const toggle = useCallback(async (productId) => {
    if (!customer || !token) return;
    if (pending.has(productId)) return;
    const alreadyIn = wishlistIds.has(productId);
    setWishlistIds(prev => { const n = new Set(prev); alreadyIn ? n.delete(productId) : n.add(productId); return n; });
    setPending(prev => new Set(prev).add(productId));
    try {
      await fetch(
        `${process.env.REACT_APP_API_URL}/api/customers/me/wishlist/${productId}`,
        { method: alreadyIn ? "DELETE" : "POST", headers: { Authorization: `Bearer ${token}` } }
      );
    } catch {
      setWishlistIds(prev => { const n = new Set(prev); alreadyIn ? n.add(productId) : n.delete(productId); return n; });
    } finally {
      setPending(prev => { const n = new Set(prev); n.delete(productId); return n; });
    }
  }, [customer, token, wishlistIds, pending]);

  return { wishlistIds, toggle, isLoggedIn: !!customer };
}

/* ── WishlistBtn — shows modal when not logged in ── */
function WishlistBtn({ productId, wishlistIds, toggle, isLoggedIn, onRequireAuth }) {
  const inWishlist = wishlistIds.has(productId);

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isLoggedIn) {
      onRequireAuth(productId);
      return;
    }
    toggle(productId);
  };

  return (
    <button
      className={`prod-card__wishlist${inWishlist ? " prod-card__wishlist--active" : ""}`}
      onClick={handleClick}
      aria-label={inWishlist ? "Remove from wishlist" : "Add to wishlist"}
      title={isLoggedIn ? (inWishlist ? "Remove from wishlist" : "Add to wishlist") : "Sign in to save to wishlist"}
    >
      <FiHeart size={15} />
    </button>
  );
}

/* ── ProductCard ── */
function ProductCard({ product, view, wishlistIds, toggleWishlist, isLoggedIn, onRequireAuth }) {
  const { addToCart, cart } = useCart();
  const inCart     = cart.some(i => i.id === product.id);
  const stock      = stockLabel(product.quantity, product.low_stock_alert || 5);
  const price      = Number(product.sale_price || product.price);
  const oldPrice   = product.sale_price ? Number(product.price) : null;
  const imgSrc     = shopImg(product.image1);
  const outOfStock = product.quantity < 1;

  const handleAdd = (e) => { e.preventDefault(); if (outOfStock) return; addToCart(product, 1); };

  if (view === "list") {
    return (
      <Link to={`/shop/${product.slug}`} className="prod-card prod-card--list">
        <div className="prod-card__img-wrap prod-card__img-wrap--list">
          {imgSrc ? <img src={imgSrc} alt={product.name} loading="lazy" /> : <div className="prod-card__no-img"><FiPackage size={32} /></div>}
          {product.discount_percent > 0 && <span className="prod-card__badge">-{Math.round(product.discount_percent)}%</span>}
        </div>
        <div className="prod-card__body prod-card__body--list">
          <div className="prod-card__meta">
            {product.category_name && <span className="prod-card__cat">{product.category_name}</span>}
            {product.car_make && (
              <span className="prod-card__fit">
                {product.car_make} {product.car_model}{" "}
                {product.car_year_from && `(${product.car_year_from}${product.car_year_to ? `–${product.car_year_to}` : "+"})`}
              </span>
            )}
          </div>
          <h3 className="prod-card__name">{product.name}</h3>
          {product.part_number && <p className="prod-card__sku">Part #: {product.part_number}</p>}
          <div className="prod-card__footer">
            <div className="prod-card__prices">
              <span className="prod-card__price">{fmt(price)}</span>
              {oldPrice && <span className="prod-card__old">{fmt(oldPrice)}</span>}
            </div>
            <span className={`prod-card__stock ${stock.cls}`}>{stock.label}</span>
            <WishlistBtn
              productId={product.id}
              wishlistIds={wishlistIds}
              toggle={toggleWishlist}
              isLoggedIn={isLoggedIn}
              onRequireAuth={onRequireAuth}
            />
            <button className={`prod-card__add ${inCart ? "prod-card__add--done" : ""}`} onClick={handleAdd} disabled={outOfStock}>
              <FiShoppingCart size={15} />{inCart ? "In Cart" : "Add"}
            </button>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link to={`/shop/${product.slug}`} className="prod-card">
      <div className="prod-card__img-wrap">
        {imgSrc ? <img src={imgSrc} alt={product.name} loading="lazy" /> : <div className="prod-card__no-img"><FiPackage size={40} /></div>}
        {product.discount_percent > 0 && <span className="prod-card__badge">-{Math.round(product.discount_percent)}%</span>}
        {product.is_featured === 1 && <span className="prod-card__featured"><FiStar size={11} /> Featured</span>}
        <WishlistBtn
          productId={product.id}
          wishlistIds={wishlistIds}
          toggle={toggleWishlist}
          isLoggedIn={isLoggedIn}
          onRequireAuth={onRequireAuth}
        />
        <button
          className={`prod-card__quick-add ${inCart ? "prod-card__quick-add--done" : ""}`}
          onClick={handleAdd} disabled={outOfStock}
          title={outOfStock ? "Out of stock" : "Add to cart"}
        >
          <FiShoppingCart size={16} />
          {outOfStock ? "Out of stock" : inCart ? "In Cart" : "Add to Cart"}
        </button>
      </div>
      <div className="prod-card__body">
        {product.category_name && <span className="prod-card__cat">{product.category_name}</span>}
        <h3 className="prod-card__name">{product.name}</h3>
        {product.car_make && (
          <p className="prod-card__fit">
            {product.car_make} {product.car_model}
            {product.car_year_from && ` (${product.car_year_from}${product.car_year_to ? `–${product.car_year_to}` : "+"})`}
          </p>
        )}
        {product.part_number && <p className="prod-card__sku">Part #: {product.part_number}</p>}
        <div className="prod-card__footer">
          <div className="prod-card__prices">
            <span className="prod-card__price">{fmt(price)}</span>
            {oldPrice && <span className="prod-card__old">{fmt(oldPrice)}</span>}
          </div>
          <span className={`prod-card__stock ${stock.cls}`}>{stock.label}</span>
        </div>
      </div>
    </Link>
  );
}

/* ══════════════════════════════════════
   SHOP PAGE
   ══════════════════════════════════════ */
export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products,    setProducts]    = useState([]);
  const [categories,  setCategories]  = useState([]);
  const [pagination,  setPagination]  = useState({ page: 1, total: 0, pages: 1 });
  const [loading,     setLoading]     = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [view,        setView]        = useState("grid");

  /* ── Wishlist auth modal state ── */
  const [authModalOpen,      setAuthModalOpen]      = useState(false);
  const [pendingWishlistId,  setPendingWishlistId]  = useState(null);

  const { wishlistIds, toggle: toggleWishlist, isLoggedIn } = useWishlist();
  const { customer } = useCustomer();

  const searchVal = searchParams.get("search")   || "";
  const catVal    = searchParams.get("category") || "";
  const makeVal   = searchParams.get("make")     || "";
  const sortVal   = searchParams.get("sort")     || "newest";
  const pageVal   = parseInt(searchParams.get("page") || "1");

  const [searchDraft, setSearchDraft] = useState(searchVal);
  const searchTimer = useRef(null);

  /* When user successfully logs in via the modal, add the pending item */
  const handleAuthSuccess = useCallback(() => {
    setAuthModalOpen(false);
    if (pendingWishlistId !== null) {
      toggleWishlist(pendingWishlistId);
      setPendingWishlistId(null);
    }
  }, [pendingWishlistId, toggleWishlist]);

  /* Called by WishlistBtn when user is not logged in */
  const handleRequireAuth = useCallback((productId) => {
    setPendingWishlistId(productId);
    setAuthModalOpen(true);
  }, []);

  /* Close modal */
  const handleCloseAuthModal = useCallback(() => {
    setAuthModalOpen(false);
    setPendingWishlistId(null);
  }, []);

  /* If customer logs in through any other means while modal is open, close it */
  useEffect(() => {
    if (customer && authModalOpen) {
      handleAuthSuccess();
    }
  }, [customer]);

  /* ── Fetch products ── */
  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams();
    if (searchVal) q.set("search",   searchVal);
    if (catVal)    q.set("category", catVal);
    if (makeVal)   q.set("make",     makeVal);
    if (sortVal)   q.set("sort",     sortVal);
    q.set("page",  pageVal);
    q.set("limit", "20");

    fetch(`${process.env.REACT_APP_API_URL}/api/shop/products?${q}`)
      .then(r    => r.json())
      .then(data => {
        setProducts(data.products   || []);
        setPagination(data.pagination || { page: 1, total: 0, pages: 1 });
      })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [searchVal, catVal, makeVal, sortVal, pageVal]);

  /* ── Fetch categories ── */
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/shop/categories`)
      .then(r    => r.json())
      .then(data => setCategories(Array.isArray(data) ? data : []))
      .catch(console.error);
  }, []);

  /* ── Helpers ── */
  const setParam = (key, val) => {
    const next = new URLSearchParams(searchParams);
    if (val) next.set(key, val); else next.delete(key);
    next.delete("page");
    setSearchParams(next);
  };

  const handleSearch = (e) => {
    const v = e.target.value;
    setSearchDraft(v);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setParam("search", v), 400);
  };

  const handleSearchClear = () => { setSearchDraft(""); setParam("search", ""); };
  const handlePage = (p) => {
    const next = new URLSearchParams(searchParams);
    next.set("page", p);
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const activeFilters = [
    catVal  && { key: "category", label: `Category: ${categories.find(c => c.slug === catVal)?.name || catVal}` },
    makeVal && { key: "make",     label: `Make: ${makeVal}` },
    searchVal && { key: "search", label: `Search: "${searchVal}"` },
  ].filter(Boolean);

  return (
    <main className="shop">

      {/* Wishlist auth modal */}
      {authModalOpen && (
        <WishlistAuthModal
          onClose={handleCloseAuthModal}
          onSuccess={handleAuthSuccess}
        />
      )}

      {/* ══ HERO ══ */}
      <div className="shop-hero">
        <div className="shop-hero__bg-image" />
        <div className="shop-hero__overlay" />
        <div className="container shop-hero__inner">
          <div className="shop-hero__left">
            <div className="shop-hero__tag">SPARE PARTS STORE</div>
            <h1 className="shop-hero__title">
              FIND THE RIGHT<br /><span>PART FOR YOUR CAR</span>
            </h1>
            <p className="shop-hero__sub">Quality spare parts &amp; accessories — delivered nationwide</p>
          </div>
        </div>
        <div className="shop-hero__mini-topbar">
          <ShopTopBar searchProps={{ value: searchDraft, onChange: handleSearch, onClear: handleSearchClear }} />
        </div>
      </div>

      {/* ══ BODY ══ */}
      <div className="container shop-body">

        {/* ── Sidebar ── */}
        <aside className={`shop-sidebar ${sidebarOpen ? "shop-sidebar--open" : ""}`}>
          <div className="shop-sidebar__head">
            <span>Filters</span>
            <button className="shop-sidebar__close" onClick={() => setSidebarOpen(false)}><FiX size={18} /></button>
          </div>

          {/* Categories */}
          <div className="shop-sidebar__section">
            <h4 className="shop-sidebar__label"><FiTag size={13} /> Categories</h4>
            <ul className="shop-sidebar__cats">
              <li>
                <button className={`shop-sidebar__cat ${!catVal ? "active" : ""}`} onClick={() => setParam("category", "")}>
                  All Parts <span>{categories.reduce((s, c) => s + (c.product_count || 0), 0)}</span>
                </button>
              </li>
              {categories.map(cat => (
                <li key={cat.id}>
                  <button className={`shop-sidebar__cat ${catVal === cat.slug ? "active" : ""}`} onClick={() => setParam("category", cat.slug)}>
                    {cat.name} <span>{cat.product_count}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* ── Car Brand filter ── */}
          <div className="shop-sidebar__section">
            <h4 className="shop-sidebar__label">🚗 Shop by Brand</h4>
            <div className="shop-sidebar__brands">
              {CAR_BRANDS.map(brand => (
                <button
                  key={brand.name}
                  className={`shop-sidebar__brand-btn${makeVal === brand.name ? " active" : ""}`}
                  onClick={() => setParam("make", makeVal === brand.name ? "" : brand.name)}
                  title={brand.name}
                >
                  <img src={brand.src} alt={brand.name} />
                  <span>{brand.name}</span>
                </button>
              ))}
            </div>
          </div>
        </aside>

        {sidebarOpen && <div className="shop-sidebar__overlay" onClick={() => setSidebarOpen(false)} />}

        {/* ── Main ── */}
        <div className="shop-main">

          {/* Toolbar */}
          <div className="shop-toolbar">
            <button className="shop-toolbar__filter-btn" onClick={() => setSidebarOpen(true)}>
              <FiFilter size={15} /> Filters
              {activeFilters.length > 0 && <span className="shop-toolbar__filter-count">{activeFilters.length}</span>}
            </button>

            {activeFilters.length > 0 && (
              <div className="shop-toolbar__active-filters">
                {activeFilters.map(f => (
                  <span key={f.key} className="shop-toolbar__chip">
                    {f.label}
                    <button onClick={() => { setParam(f.key, ""); if (f.key === "search") setSearchDraft(""); }}>
                      <FiX size={11} />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="shop-toolbar__right">
              <p className="shop-toolbar__count">
                {loading ? "Loading…" : `${pagination.total} product${pagination.total !== 1 ? "s" : ""}`}
              </p>
              <select className="shop-toolbar__sort" value={sortVal} onChange={e => setParam("sort", e.target.value)}>
                <option value="newest">Newest</option>
                <option value="price_asc">Price: Low → High</option>
                <option value="price_desc">Price: High → Low</option>
                <option value="name_asc">Name A–Z</option>
              </select>
              <div className="shop-toolbar__view">
                <button className={view === "grid" ? "active" : ""} onClick={() => setView("grid")} title="Grid view"><FiGrid size={16} /></button>
                <button className={view === "list" ? "active" : ""} onClick={() => setView("list")} title="List view"><FiList size={16} /></button>
              </div>
            </div>
          </div>

          {/* Products */}
          {loading ? (
            <div className={`shop-grid${view === "list" ? " shop-grid--list" : ""}`}>
              {Array.from({ length: 8 }).map((_, i) => <div key={i} className="prod-skeleton" />)}
            </div>
          ) : products.length === 0 ? (
            <div className="shop-empty">
              <FiPackage size={52} />
              <h3>No products found</h3>
              <p>Try adjusting your search or filters.</p>
              <button className="btn-outline" onClick={() => { setSearchParams({}); setSearchDraft(""); }}>Clear all filters</button>
            </div>
          ) : (
            <div className={`shop-grid${view === "list" ? " shop-grid--list" : ""}`}>
              {products.map(p => (
                <ProductCard
                  key={p.id}
                  product={p}
                  view={view}
                  wishlistIds={wishlistIds}
                  toggleWishlist={toggleWishlist}
                  isLoggedIn={isLoggedIn}
                  onRequireAuth={handleRequireAuth}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="shop-pagination">
              <button className="shop-pagination__btn" disabled={pageVal <= 1} onClick={() => handlePage(pageVal - 1)}>← Prev</button>
              {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(p => (
                <button key={p} className={`shop-pagination__btn ${p === pageVal ? "active" : ""}`} onClick={() => handlePage(p)}>{p}</button>
              ))}
              <button className="shop-pagination__btn" disabled={pageVal >= pagination.pages} onClick={() => handlePage(pageVal + 1)}>Next →</button>
            </div>
          )}
        </div>
      </div>

      {/* ══ VEHICLES MARQUEE — SVG logos ══ */}
      <section className="home-vehicles-section">
        <div className="home-vehicles__label-bar">
          <div className="container">
            <span className="home-vehicles__heading">THE VEHICLES WE SPECIALISE ON</span>
          </div>
        </div>
        <div className="home-vehicles__marquee">
          <div className="home-vehicles__track">
            {[...MARQUEE_BRANDS, ...MARQUEE_BRANDS].map((brand, i) => (
              <div key={i} className="home-vehicles__logo">
                <img
                  src={brand.svg}
                  alt={brand.name}
                  title={brand.name}
                  loading="lazy"
                  draggable="false"
                  onDragStart={e => e.preventDefault()}
                  onContextMenu={e => e.preventDefault()}
                />
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}